var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger, } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dot } from "lucide-react";
import { cn, formatIntlDecimal } from "@/lib/utils";
export var Filters = function (_a) {
    var _b = _a.uiState, bedrooms = _b.bedrooms, bathrooms = _b.bathrooms, unitSize = _b.unitSize, priceRange = _b.priceRange, availableUnitsOnly = _b.availableUnitsOnly, priceRangeLabel = _b.priceRangeLabel, unitSizeLabel = _b.unitSizeLabel, floorplanType = _b.floorplanType, _c = _a.setters, setBedrooms = _c.setBedrooms, setBathrooms = _c.setBathrooms, setUnitSize = _c.setUnitSize, setPriceRange = _c.setPriceRange, setAvailableUnitsOnly = _c.setAvailableUnitsOnly, setPriceRangeLabel = _c.setPriceRangeLabel, setUnitSizeLabel = _c.setUnitSizeLabel, setFloorplanType = _c.setFloorplanType, filters = _a.filters, setFilter = _a.setFilter, onReset = _a.onReset, labels = _a.labels, bedroomTypes = _a.bedroomTypes, numberOfBathrooms = _a.numberOfBathrooms, floorplanTypes = _a.floorplanTypes, _d = _a.showUnavailableFloorplans, showUnavailableFloorplans = _d === void 0 ? false : _d;
    var _e = useState(false), unitSizeOpen = _e[0], setUnitSizeOpen = _e[1];
    var _f = useState(false), priceRangeOpen = _f[0], setPriceRangeOpen = _f[1];
    var _g = useState(""), priceRangeError = _g[0], setPriceRangeError = _g[1];
    var _h = useState(""), unitSizeError = _h[0], setUnitSizeError = _h[1];
    useEffect(function () {
        handlePriceRangeLabel();
        handleUnitSizeLabel();
    }, [
        filters.priceMin,
        filters.priceMax,
        filters.unitSizeMin,
        filters.unitSizeMax,
    ]);
    var handleBedroomChange = function (value) {
        setBedrooms(value);
        setFilter("bedrooms", value);
    };
    var handleBathroomChange = function (value) {
        setBathrooms(value);
        setFilter("bathrooms", value);
    };
    var handleFloorplanTypeChange = function (value) {
        setFloorplanType(value);
        setFilter("floorplanType", value);
    };
    var handleUnitSizeChange = function (key, value) {
        var _a;
        if (value.length > 5)
            return;
        var newUnitSize = __assign(__assign({}, unitSize), (_a = {}, _a[key] = value, _a));
        setUnitSize(newUnitSize);
        setUnitSizeError(determineErrorMessage(key, value, {
            min: newUnitSize.min,
            max: newUnitSize.max,
            unit: labels.sqft,
        }));
    };
    var handleUnitSizePopoverClose = function () {
        if (!unitSizeError) {
            setFilter("unitSizeMin", unitSize.min || undefined);
            setFilter("unitSizeMax", unitSize.max || undefined);
        }
        handleUnitSizeLabel();
    };
    var handlePriceRangeChange = function (key, value) {
        var _a;
        if (value.length > 5)
            return;
        var newPriceRange = __assign(__assign({}, priceRange), (_a = {}, _a[key] = value, _a));
        setPriceRange(newPriceRange);
        setPriceRangeError(determineErrorMessage(key, value, {
            min: newPriceRange.min,
            max: newPriceRange.max,
            unit: "price",
        }));
    };
    var handlePriceRangePopoverClose = function () {
        if (!priceRangeError) {
            setFilter("priceMin", priceRange.min || undefined);
            setFilter("priceMax", priceRange.max || undefined);
        }
        handlePriceRangeLabel();
    };
    var handleAvailableUnitsOnlyChange = function (checked) {
        setAvailableUnitsOnly(checked);
    };
    var determineErrorMessage = function (key, value, _a) {
        var min = _a.min, max = _a.max, unit = _a.unit;
        var newValue = Number(value);
        var otherValue = Number(key === "min" ? max : min);
        if (!newValue || !otherValue)
            return "";
        if ((key === "min" && newValue > otherValue && otherValue !== 0) ||
            (key === "max" && otherValue > newValue && newValue !== 0)) {
            return "Minimum ".concat(unit, " cannot exceed maximum ").concat(unit);
        }
        return "";
    };
    var handleUnitSizeLabel = function () {
        setUnitSizeLabel(formatLabel(formatIntlDecimal(unitSize.min), formatIntlDecimal(unitSize.max), {
            defaultLabel: labels.unit,
            suffix: " ".concat(labels.sqft),
        }));
    };
    var handlePriceRangeLabel = function () {
        setPriceRangeLabel(formatLabel(formatIntlDecimal(priceRange.min), formatIntlDecimal(priceRange.max), {
            defaultLabel: labels.price,
            prefix: "$",
        }));
    };
    var formatLabel = function (min, max, _a) {
        var defaultLabel = _a.defaultLabel, _b = _a.prefix, prefix = _b === void 0 ? "" : _b, _c = _a.suffix, suffix = _c === void 0 ? "" : _c;
        if (!min && !max)
            return defaultLabel;
        if (!min || min === "0")
            return "".concat(prefix, "0 - ").concat(prefix).concat(max).concat(suffix);
        if (!max || max === "0")
            return "".concat(prefix).concat(min, "+").concat(suffix);
        return "".concat(prefix).concat(min, " - ").concat(prefix).concat(max).concat(suffix);
    };
    return (React.createElement("div", { className: "w-full flex flex-wrap gap-4 pt-6 pb-12" },
        floorplanTypes.length > 0 && (React.createElement(Select, { value: floorplanType, onValueChange: handleFloorplanTypeChange },
            React.createElement(SelectTrigger, { className: "w-[130px] md:w-[140px]" },
                React.createElement(SelectValue, { placeholder: "Unit Type" })),
            React.createElement(SelectContent, null,
                React.createElement(SelectItem, { value: "any" }, "Any"),
                floorplanTypes.map(function (type) { return (React.createElement(SelectItem, { key: type, value: type }, formatFloorplanTypeLabel(type))); })))),
        React.createElement(Select, { value: bedrooms, onValueChange: handleBedroomChange },
            React.createElement(SelectTrigger, { className: "w-[130px] md:w-[140px]" },
                React.createElement(SelectValue, { placeholder: "Bedrooms" })),
            React.createElement(SelectContent, null,
                React.createElement(SelectItem, { value: "any" }, "Any"),
                bedroomOptions(bedroomTypes))),
        React.createElement(Select, { value: bathrooms, onValueChange: handleBathroomChange },
            React.createElement(SelectTrigger, { className: "w-[130px] md:w-[140px]" },
                React.createElement(SelectValue, { placeholder: "Bathrooms" })),
            React.createElement(SelectContent, null,
                React.createElement(SelectItem, { value: "any" }, "Any"),
                bathroomOptions(numberOfBathrooms))),
        React.createElement(Popover, { open: unitSizeOpen, onOpenChange: function (open) {
                setUnitSizeOpen(open);
                if (!open) {
                    handleUnitSizePopoverClose();
                }
            } },
            React.createElement(PopoverTrigger, { asChild: true, className: cn(unitSizeError && "border-red-500") },
                React.createElement(Button, { variant: "outline" }, unitSizeLabel)),
            React.createElement(PopoverContent, { className: "w-80" },
                React.createElement("div", { className: "grid gap-4" },
                    React.createElement("div", { className: "grid grid-cols-2 gap-2" },
                        React.createElement("div", null,
                            React.createElement(Label, { htmlFor: "unitSizeMin" }, "Min (sqft)"),
                            React.createElement(Input, { className: cn(unitSizeError && "border-red-500"), id: "unitSizeMin", step: 25, value: unitSize.min, onChange: function (e) { return handleUnitSizeChange("min", e.target.value); }, type: "number" })),
                        React.createElement("div", null,
                            React.createElement(Label, { htmlFor: "unitSizeMax" }, "Max (sqft)"),
                            React.createElement(Input, { className: cn(unitSizeError && "border-red-500"), id: "unitSizeMax", step: 25, value: unitSize.max, onChange: function (e) { return handleUnitSizeChange("max", e.target.value); }, type: "number" }))),
                    unitSizeError && (React.createElement("p", { className: "text-sm text-red-500" }, unitSizeError))))),
        React.createElement(Popover, { open: priceRangeOpen, onOpenChange: function (open) {
                setPriceRangeOpen(open);
                if (!open) {
                    handlePriceRangePopoverClose();
                }
            } },
            React.createElement(PopoverTrigger, { className: cn(priceRangeError && "border-red-500"), asChild: true },
                React.createElement(Button, { variant: "outline" }, priceRangeLabel)),
            React.createElement(PopoverContent, { className: "w-80" },
                React.createElement("div", { className: "grid gap-4" },
                    React.createElement("div", { className: "grid grid-cols-2 gap-2" },
                        React.createElement("div", null,
                            React.createElement(Label, { htmlFor: "priceMin" }, "Min ($)"),
                            React.createElement(Input, { id: "priceMin", step: 100, value: priceRange.min, onChange: function (e) {
                                    return handlePriceRangeChange("min", e.target.value);
                                }, type: "number", className: cn(priceRangeError && "border-red-500") })),
                        React.createElement("div", null,
                            React.createElement(Label, { htmlFor: "priceMax" }, "Max ($)"),
                            React.createElement(Input, { id: "priceMax", step: 100, value: priceRange.max, onChange: function (e) {
                                    return handlePriceRangeChange("max", e.target.value);
                                }, type: "number", className: cn(priceRangeError && "border-red-500") }))),
                    priceRangeError && (React.createElement("p", { className: "text-sm text-red-500" }, priceRangeError))))),
        showUnavailableFloorplans && (React.createElement(Popover, null,
            React.createElement(PopoverTrigger, { className: "xl:ml-auto", asChild: true },
                React.createElement(Button, { variant: "outline" },
                    (Object.values(filters).some(function (value) { return value !== undefined; }) ||
                        availableUnitsOnly) && React.createElement(Dot, { strokeWidth: 10 }),
                    "More Filters")),
            React.createElement(PopoverContent, { className: "w-44" },
                React.createElement("div", { className: "grid gap-4 place-items-center" },
                    React.createElement("div", { className: "flex items-center space-x-2" },
                        React.createElement(Label, { htmlFor: "availableUnitsOnly" }, "Available units only"),
                        React.createElement(Switch, { id: "availableUnitsOnly", checked: availableUnitsOnly, onCheckedChange: handleAvailableUnitsOnlyChange })),
                    React.createElement(Button, { variant: "link", onClick: onReset }, "Reset all filters")))))));
};
function bathroomOptions(numberOfBathrooms) {
    return Array.from({ length: numberOfBathrooms }, function (_, k) { return k + 1; }).map(function (n) {
        return React.createElement(SelectItem, { key: n, value: String(n) },
            n,
            " Bathroom");
    });
}
function bedroomOptions(bedroomTypes) {
    return bedroomTypes.map(function (type) {
        if (type < 1) {
            return React.createElement(SelectItem, { key: type, value: String(type) }, "Studio");
        }
        else {
            return React.createElement(SelectItem, { key: type, value: String(type) },
                type,
                " Bedroom");
        }
    });
}
function formatFloorplanTypeLabel(type) {
    // Capitalize first letter and handle common cases
    return type.charAt(0).toUpperCase() + type.slice(1);
}
